// SAICRAFT — Sai's Block Quest. Game bootstrap, render loop and state machine.
import * as THREE from 'three';
import { fogFromCamera } from './fog.js'; // patches three's fog chunks before any material compiles
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { buildAtlas, makeWaterTexture, makeLavaTexture, tileDataURL } from './textures.js';
import { createVoxelMaterials } from './voxel.js';
import { setAtlasMaterial } from './models.js';
import { Sky } from './sky.js';
import { Particles, Rings, Beams, WorldUI } from './effects.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { Input } from './input.js';
import { CameraRig } from './camera.js';
import { saves } from './save.js';
import { QuestionEngine, TOPICS } from './questions.js';
import { Quiz } from './quiz.js';
import { UI } from './ui.js';
import { MenuScene } from './menu.js';
import { Level } from './level.js';
import { ExploreMode } from './explore.js';
import { BlitzMode } from './blitz.js';
import { THEMES, WORLDS, nextLevelId, parseLevelId } from './themes.js';
import { Character } from './character.js';
import { paintSkin, paintNPC } from './skins.js';
import { CATALOG, catalogItem, levelInfo, rankFor, BADGES, DAILY, unlockMet, isLevelUnlocked } from './progression.js';
import { todayStr, daysBetween, isTouchDevice, fmtNum, wait, escapeHtml, withName } from './util.js';
import { ICON } from './icons.js';
import { installTestHooks } from './debug.js';
import { Accounts } from './accounts.js';
import { Multiplayer, helloFrame } from './multiplayer.js';
import { RoomClient } from './room-client.js';
import { listsFromSnapshot } from './room-mirror.js';
import { identity } from './identity.js';
import { joinOverlayHTML } from './friends-ui.js';

const TIPS = [
  'Tip: 7 × 8 = 56 — just remember 5, 6, 7, 8!',
  'Tip: hold JUMP for a big jump, tap it for a little hop.',
  'Tip: Lucky Blocks can hold coins, gems or power-ups!',
  'Tip: stomp on slimes to squash them!',
  'Tip: look for the beam of light — it marks the secret star ⭐',
  'Tip: right answers in a row build your 🔥 streak!',
  'Tip: anything times 10? Just add a zero!',
];

class Game {
  constructor() {
    this.state = 'loading';
    this.transition=0;
    this.paused = false;
    this.level = null;
    this.blitz = null;
    this.mp = null;
    this.time = 0;
    this.skinCache = new Map();
    this.fpsAcc = 0;
    this.fpsFrames = 0;
    this.qualityLevel = null;
    this.badgeQueue = [];
    this.THREE = THREE;
  }

  progress(text, f) {
    const fill = document.getElementById('load-fill');
    if (fill) fill.style.width = Math.round(f * 100) + '%';
    if (text) document.getElementById('load-tip').textContent = text;
  }

  async init() {
    this.progress('Waking up the Block Realm…', 0.08);
    await nextFrame();
    const canvas = document.getElementById('game');
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', stencil: false });
    } catch (e) {
      document.getElementById('load-tip').innerHTML = 'Oh no! This device can’t run 3D graphics (WebGL). Try Chrome, Safari or Edge on a newer device.';
      throw e;
    }
    this.renderer = renderer;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(62, 1, 0.1, 1200);
    this.scene.fog = new THREE.Fog('#cdeaff', 75, 260);
    this.hemi = new THREE.HemisphereLight('#d6ecff', '#7a6a4a', 1.35);
    this.scene.add(this.hemi);
    this.sun = new THREE.DirectionalLight('#fff1d6', 2.6);
    this.sun.castShadow = true;
    const sc = this.sun.shadow.camera;
    sc.left = -34;
    sc.right = 34;
    sc.top = 34;
    sc.bottom = -34;
    sc.near = 1;
    sc.far = 180;
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.04;
    this.sun.shadow.radius = 2.5;
    this.scene.add(this.sun, this.sun.target);
    this.sunDir = new THREE.Vector3(0.5, 0.62, 0.35).normalize();
    this.sky = new Sky(this.scene);

    this.progress('Painting pixel textures…', 0.25);
    await nextFrame();
    const tex = buildAtlas(renderer);
    this.waterTex = makeWaterTexture();
    this.lavaTex = makeLavaTexture();
    this.mats = createVoxelMaterials(tex, this.waterTex, this.lavaTex);
    this.mats.solid.userData.shared = true;
    setAtlasMaterial(this.mats.solid);
    document.documentElement.style.setProperty('--tex-dirt', `url(${tileDataURL('dirt', 4)})`);
    document.documentElement.style.setProperty('--tex-planks', `url(${tileDataURL('planks', 4)})`);
    document.documentElement.style.setProperty('--tex-stone', `url(${tileDataURL('stone', 4)})`);
    document.documentElement.style.setProperty('--tex-grass', `url(${tileDataURL('grass_side', 4)})`);

    this.progress('Loading your save…', 0.4);
    this.save = saves;
    saves.load();
    haptics.bind(() => saves.data.settings.haptics);
    this.qe = new QuestionEngine(() => saves.data);
    this.fx = {
      particles: new Particles(this.scene),
      rings: new Rings(this.scene),
      beams: new Beams(this.scene),
      worldUI: new WorldUI(document.getElementById('world-ui'), this.camera),
    };
    this.input = new Input(canvas, document.getElementById('ui'));
    if (isTouchDevice()) this.input.setTouchMode(true);
    this.camRig = new CameraRig(this.camera);
    this.camRig.setDistance(saves.data.settings.cameraDistance);
    this.ui = new UI(this);
    this.accounts = new Accounts(this);
    this.accounts.boot();
    this.input.onTouchModeChange = () => this.explore?.refreshHUD();
    this.input.onLayoutCancel = () => this.ui.act('touchLayoutCancel', {});
    this.input.layout.apply();
    this.quiz = new Quiz(document.getElementById('ui'), this.qe, () => saves.data, (r) => this.onAnswer(r.correct, r.firstTry, r.firstAttempt, r.challenge));
    const prof = paintNPC('professor');
    this.quiz.setAvatar(prof.canvas);
    audio.setVolumes(saves.data.settings.music, saves.data.settings.sfx);

    this.progress('Building sky islands…', 0.6);
    await nextFrame();
    this.setupComposer();
    this.applyQuality();
    this.onResize();
    window.addEventListener('resize', () => this.onResize());
    window.addEventListener('orientationchange', () => setTimeout(() => this.onResize(), 200));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && (this.state === 'level' || this.state === 'blitz' || this.state === 'explore') && !this.paused) this.pause();
    });

    this.menuScene = new MenuScene(this);
    this.menuScene.setTheme(WORLDS[Math.min(WORLDS.length - 1, this.highestWorld() - 1)].theme);
    this.menuScene.setCharacter(this.makePlayerCharacter(), saves.data.equip.pet);
    this.menuScene.setMode('title');
    this.menuScene.snapCamera();
    this.state = 'boot';
    this.progress('Compiling shaders…', 0.82);
    await nextFrame();
    this.renderer.compile(this.scene, this.camera);
    this.last = performance.now();
    requestAnimationFrame((t) => this.loop(t));
    this.progress(TIPS[Math.floor(Math.random() * TIPS.length)], 1);
    await wait(250);
    // Finish cloud hydration before enabling play, so every mode uses the same save.
    await saves.syncOnBoot().then((r) => {
      if (r === 'pulled') {
        this.refreshCharacter();
        this.camRig.setDistance(saves.data.settings.cameraDistance);
        audio.setVolumes(saves.data.settings.music, saves.data.settings.sfx);
        if (this.state === 'menu' && this.ui.screen === 'main') this.ui.showMain();
        this.ui.toast('☁️ Loaded your latest save from the cloud!', 'blue');
      }
      if (r === 'waiting') this.ui.toast('We couldn’t load your game yet. Check the internet, then open SAICRAFT again.', 'blue', 6000);
    });
    this.showStartButton();
  }

  showStartButton() {
    const load = document.getElementById('loading');
    const inner = load.querySelector('.load-inner');
    const bar = inner.querySelector('.load-bar');
    bar.style.display = 'none';
    const btn = document.createElement('button');
    btn.className = 'btn btn-green btn-huge start-btn';
    btn.innerHTML = `▶ ${this.save.fresh ? 'START' : 'PLAY'}`;
    inner.insertBefore(btn, inner.querySelector('.load-tip'));
    load.classList.add('ready');
    const go = async () => {
      btn.disabled = true;
      audio.unlock();
      haptics.unlock();
      audio.play('go');
      load.classList.add('hide');
      setTimeout(() => load.remove(), 700);
      await this.firstRun();
    };
    btn.addEventListener('click', go, { once: true });
  }

  async firstRun() {
    // New and returning players say who's playing once, so their game follows them to any device.
    if (this.accounts.needsWho()) {
      this.enterMenuScene('title');
      await this.accounts.who({ canSkip: true });
    }
    const s = saves.data;
    s.stats.sessions++;
    const today = todayStr();
    if (s.daily.lastPlay !== today) {
      s.daily.lastPlay = today;
    }
    saves.persist();
    this.showMenu();
    if (!s.seenIntro) {
      audio.playMusic('title');
      await this.ui.showIntro();
      s.seenIntro = true;
      saves.persist();
    }
    if (s.daily.lastClaim !== today) setTimeout(() => this.state === 'menu' && !this.ui.modalOpen && this.ui.showDaily(), 600);
  }

  highestWorld() {
    let w = 1;
    for (const W of WORLDS) if (isLevelUnlocked(saves.data, `${W.id}-1`)) w = W.id;
    return w;
  }

  // ---------------- rendering setup ----------------
  setupComposer() {
    const rt = new THREE.WebGLRenderTarget(16, 16, { type: THREE.HalfFloatType, samples: 4 });
    this.composer = new EffectComposer(this.renderer, rt);
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 0.42, 0.45, 0.92);
    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
  }

  applyQuality(force) {
    const q = force || saves.data.settings.quality;
    let lvl = q;
    if (q === 'auto') lvl = this.qualityLevel || (isTouchDevice() ? 'medium' : 'high');
    this.qualityLevel = lvl;
    const dpr = window.devicePixelRatio || 1;
    const pr = lvl === 'high' ? Math.min(2, dpr) : lvl === 'medium' ? Math.min(1.5, dpr) : Math.min(1, dpr);
    this.pixelRatio = pr;
    this.renderer.setPixelRatio(pr);
    this.composer.setPixelRatio(pr);
    this.renderer.shadowMap.enabled = lvl !== 'low';
    const ms = lvl === 'high' ? 2048 : 1024;
    if (this.sun.shadow.mapSize.x !== ms) {
      this.sun.shadow.mapSize.set(ms, ms);
      if (this.sun.shadow.map) {
        this.sun.shadow.map.dispose();
        this.sun.shadow.map = null;
      }
    }
    this.bloom.enabled = lvl !== 'low';
    this.scene.traverse((o) => {
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => (m.needsUpdate = true));
      }
    });
    this.onResize();
  }

  onResize() {
    const w = window.innerWidth, h = window.innerHeight;
    if (!w || !h) return;
    this.camera.aspect = w / h;
    this.camera.fov = w / h < 1 ? 75 : 62;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
    this.bloom.resolution.set(w / 2, h / 2);
    this.input?.layout.apply();
  }

  applyTheme(key) {
    const t = THEMES[key];
    fogFromCamera();
    this.themeKey = key;
    this.sky.setTheme(t.sky);
    this.scene.fog.color.set(t.fog[0]);
    this.scene.fog.near = t.fog[1];
    this.scene.fog.far = t.fog[2];
    this.hemi.color.set(t.light.hemiSky);
    this.hemi.groundColor.set(t.light.hemiGround);
    this.hemi.intensity = t.light.hemi;
    this.sun.color.set(t.light.sun);
    this.sun.intensity = t.light.sunI;
    this.sunDir.set(...t.sky.sunDir).normalize();
    this.ambient = t.ambient;
  }

  // ---------------- characters ----------------
  currentSkin() {
    const s = saves.data;
    const key = `${s.equip.skin}|${s.avatar.tone}|${s.avatar.hair}|${s.avatar.style}`;
    if (!this.skinCache.has(key)) this.skinCache.set(key, paintSkin(s.equip.skin, s.avatar.tone, s.avatar.hair, s.avatar.style));
    return this.skinCache.get(key);
  }

  makePlayerCharacter() {
    const s = saves.data;
    const ch = new Character(this.currentSkin(), { name: s.name.toUpperCase(), crown: true });
    ch.setItem(s.equip.sword);
    const acc = catalogItem('skin', s.equip.skin)?.acc;
    if (acc) ch.setAccessory(acc);
    return ch;
  }

  refreshCharacter() {
    if (this.menuScene) this.menuScene.setCharacter(this.makePlayerCharacter(), saves.data.equip.pet);
  }

  topicName(t) {
    return TOPICS[t]?.name || t;
  }

  // ---------------- menus ----------------
  enterMenuScene(mode) {
    this.transition++;this.ui.fade(false);this.input.reset();
    this.disposePlay();
    this.state = 'menu';
    this.paused = false;
    this.menuScene.group.visible = true;
    this.menuScene.setMode(mode);
    this.input.showTouchControls(false);
    this.ui.hud.hide();
    this.ui.bossHud.hide();
    this.ui.blitzHud.hide();
    this.ui.hideTitleCard();
    audio.setDuck(false);
    audio.playMusic('title');
  }

  showMenu() {
    const wasMenu = this.state === 'menu';
    this.enterMenuScene('title');
    if (!wasMenu) this.menuScene.snapCamera();
    this.menuScene.setTheme(WORLDS[Math.min(WORLDS.length - 1, this.highestWorld() - 1)].theme);
    this.ui.closeModal();
    this.ui.showMain();
  }

  showWorlds(wi) {
    const wasMenu = this.state === 'menu';
    this.enterMenuScene('worlds');
    if (!wasMenu) this.menuScene.snapCamera();
    this.ui.closeModal();
    this.ui.showWorlds(wi);
  }

  showLocker() {
    this.enterMenuScene('locker');
    this.ui.showLocker();
  }

  showBadges() {
    this.enterMenuScene('worlds');
    this.ui.showBadges();
  }

  showStats() {
    this.enterMenuScene('worlds');
    this.ui.showStats();
  }

  showSettingsScreen() {
    this.enterMenuScene('worlds');
    this.ui.showSettings(false);
  }

  // ---------------- play ----------------
  disposePlay() {
    if (this.level) {
      this.level.dispose();
      this.level = null;
    }
    if (this.blitz) {
      this.blitz.dispose();
      this.blitz = null;
    }
    if (this.mp) { this.mp.dispose(); this.mp = null; }
    if (this.explore) { this.explore.dispose(); this.explore = null; }
    this.camRig.skipCine();
  }

  // Explore always opens your own world; { room } visits a friend's world instead.
  async startExplore(opts = {}) {
    const transition = ++this.transition;
    let session = null, client = null, snapshot = null;
    if (opts.room) {
      this.ui.closeModal();
      const holder = document.createElement('div');
      holder.innerHTML = joinOverlayHTML(opts.ownerName || 'your friend');
      const overlay = holder.firstElementChild;
      this.ui.root.appendChild(overlay);
      client = new RoomClient();
      try {
        snapshot = await client.open(opts.room, helloFrame(saves.data), { retry: false, timeout: 8000 });
      } catch (e) {
        client.close();
        overlay.remove();
        if (transition !== this.transition) return;
        if (e?.code === 4001) identity.setToken(null, 0);
        return this.accounts.showFriends(joinFailText(e?.code, opts.ownerName));
      }
      overlay.remove();
      if (transition !== this.transition) return client.close();
      client.retry = true; // once you've arrived, brief network blips reconnect quietly
      const lists = listsFromSnapshot(snapshot.world, { worldTime: saves.data.discovery.worldTime, worldOwner: snapshot.room.ownerId });
      session = {
        code: opts.room,
        ownerId: snapshot.room.ownerId,
        ownerName: snapshot.room.ownerName,
        world: { blocks: lists.blocks, removed: lists.removed, harvested: lists.harvested, owners: lists.owners, expansion: { devices: lists.devices || {}, home: null, animals: [] } },
      };
      this.accounts.rememberWorld(opts.room, snapshot.room.ownerName);
    }
    this.ui.closeModal(); this.ui.hideScreen(); this.disposePlay();
    this.menuScene.group.visible = false;
    this.applyTheme('plains');
    this.explore = new ExploreMode(this, { session }); this.explore.build(); this.explore.updateFog();
    if (session) this.mp = new Multiplayer(this, this.explore, { mode: 'guest', code: opts.room, client, snapshot });
    else if (identity.loggedIn && identity.tokenValid() && location.protocol !== 'file:')
      this.mp = new Multiplayer(this, this.explore, { mode: 'own', code: identity.data.roomCode });
    this.explore.refreshHUD();
    this.state = 'explore'; this.paused = false;
    this.fpsAcc = 0; this.fpsFrames = 0;
    this.input.reset(); this.input.showTouchControls(true);
    this.ui.hud.hide(); this.ui.bossHud.hide(); this.ui.blitzHud.hide();
    audio.setDuck(false); audio.playMusic('plains');

  }

  async startLevel(id) {
    const transition=++this.transition;
    this.ui.closeModal();
    this.ui.hideScreen();
    this.ui.fade(true);
    audio.stopMusic();
    await wait(300);
    if(transition!==this.transition)return;
    this.disposePlay();
    this.menuScene.group.visible = false;
    const L = new Level(this, id);
    this.applyTheme(L.themeKey);
    L.build();
    this.level = L;
    this.lastLevelId = id;
    this.state = 'level';
    this.paused = false;
    this.quiz.streak = this.quiz.streak || 0;
    this.ui.hud.show(L);
    this.input.reset();
    this.input.showTouchControls(true);
    this.renderer.compile(this.scene, this.camera);
    this.ui.fade(false);
    audio.playMusic(L.isBoss ? 'boss' : L.theme.music);
    this.fpsAcc = 0;
    this.fpsFrames = 0;
    await L.intro();
  }

  restartLevel() {
    if (this.state === 'explore') return this.startExplore();
    if (this.state === 'blitz') return this.startBlitz(this.blitz.mode);
    if (this.lastLevelId) this.startLevel(this.lastLevelId);
  }

  quitLevel() {
    this.paused = false;
    if (this.state === 'explore') return this.showMenu();
    const id = this.lastLevelId;
    this.showWorlds(id ? parseLevelId(id).w - 1 : undefined);
  }

  pause() {
    if (this.paused || !(this.state === 'level' || this.state === 'blitz' || this.state === 'explore')) return;
    if (this.level && (this.level.state === 'quiz' || this.level.state === 'win')) return;
    if (this.explore?.state === 'quiz') return;
    if (this.blitz && this.blitz.state === 'over') return;
    this.paused = true;
    this.explore?.cancelGather();
    this.input.reset();
    audio.setDuck(true);
    this.ui.showPause();
  }

  resume() {
    if (!this.paused) return;
    this.paused = false;
    audio.setDuck(false);
    this.ui.closeModal();
    this.input.reset();
    this.last = performance.now();
  }

  levelComplete(res) {
    const s = saves.data;
    const prev = s.levels[res.id] || { done: false, stars: [0, 0, 0], best: 0 };
    const firstClear = !prev.done;
    const stars = res.stars.map((x, i) => (x || prev.stars[i] ? 1 : 0));
    const newBestTime = !prev.best || res.time < prev.best;
    s.levels[res.id] = { done: true, stars, best: newBestTime ? Math.round(res.time * 10) / 10 : prev.best, coins: Math.max(prev.coins || 0, res.coins) };
    const xpFrom = levelInfo(s.xp);
    s.coins += res.coins;
    s.gems += res.gems;
    s.xp += res.xp;
    const st = s.stats;
    st.coinsTotal += res.coins;
    st.gemsTotal += res.gems;
    st.mobs += res.mobs;
    st.stomps += res.stomps;
    st.lucky += res.lucky;
    st.chests += res.chests;
    st.falls += res.falls;
    st.jumps += res.jumps;
    if (firstClear) st.levelsDone++;
    if (res.secret && !prev.stars[2] && !res.isBoss) st.secrets++;
    if (res.asked > 0 && res.firstTry === res.asked) st.perfect++;
    if (res.noDamage) st.noDamage++;
    if (stars.every(Boolean)) st.threeStars++;
    if (res.isBoss && firstClear) st.bosses++;
    const unlocks = [];
    if (res.id === '1-2' && !s.unlocks.doubleJump) {
      s.unlocks.doubleJump = true;
      unlocks.push('🦘 <b>DOUBLE JUMP</b> unlocked! Press jump again in the air!');
    }
    const next = nextLevelId(res.id);
    if (firstClear && next) {
      const { w, l } = parseLevelId(next);
      if (l === 1) unlocks.push(`🌍 <b>World ${w}: ${WORLDS[w - 1].name}</b> unlocked!`);
      else if (l === 4) unlocks.push(`👑 Boss battle unlocked: <b>${WORLDS[w - 1].boss.name}</b>!`);
      else unlocks.push(`🔓 Next level unlocked!`);
    }
    if (res.isBoss && firstClear) {
      for (const cat of Object.keys(CATALOG))
        for (const it of CATALOG[cat]) if (it.unlock === `boss:${parseLevelId(res.id).w}`) unlocks.push(`🎁 <b>${withName(it.name, escapeHtml(s.name))}</b> unlocked in your Locker!`);
    }
    const xpTo = levelInfo(s.xp);
    const levelUps = [];
    for (let lv = xpFrom.level + 1; lv <= xpTo.level; lv++) {
      const reward = 20 + lv * 5;
      s.coins += reward;
      levelUps.push({ level: lv, rankChanged: rankFor(lv).name !== rankFor(lv - 1).name, reward });
    }
    for (const cat of Object.keys(CATALOG)) for (const it of CATALOG[cat]) if (it.unlock && it.unlock.startsWith('level') && unlockMet(s, it.unlock) && xpFrom.level < parseInt(it.unlock.split(':')[1], 10)) unlocks.push(`🎁 <b>${withName(it.name, escapeHtml(s.name))}</b> unlocked in your Locker!`);
    saves.persist(true);
    this.state = 'results';
    this.input.showTouchControls(false);
    this.ui.hud.hide();
    this.ui.bossHud.hide();
    this.ui.showResults(res, { xpFrom, xpTo, levelUps, unlocks, newBestTime: newBestTime && !firstClear }).then(() => this.checkBadges());
    audio.playMusic('title');
  }

  async startBlitz(mode) {
    const transition=++this.transition;
    this.ui.closeModal();
    this.ui.hideScreen();
    this.ui.fade(true);
    audio.stopMusic();
    await wait(300);
    if(transition!==this.transition)return;
    this.disposePlay();
    this.menuScene.group.visible = false;
    const B = new BlitzMode(this, mode);
    const theme = WORLDS[this.highestWorld() - 1].theme;
    B.build(theme);
    this.blitz = B;
    this.state = 'blitz';
    this.paused = false;
    this.input.reset();
    this.input.showTouchControls(true);
    this.ui.fade(false);
    audio.playMusic('blitz');
  }

  blitzComplete(r) {
    const s = saves.data;
    const coins = Math.floor(r.score / 20);
    const gems = r.score >= 4000 ? 5 : r.score >= 2500 ? 3 : r.score >= 1200 ? 1 : 0;
    const xp = r.correct * 8 + 20;
    const newBest = r.score > (s.blitz.best || 0);
    s.blitz.best = Math.max(s.blitz.best || 0, r.score);
    s.blitz.scores = [...(s.blitz.scores || []), { s: r.score, d: todayStr(), m: r.mode }].sort((a, b) => b.s - a.s).slice(0, 10);
    s.coins += coins;
    s.gems += gems;
    const xpFrom = levelInfo(s.xp);
    s.xp += xp;
    s.stats.coinsTotal += coins;
    s.stats.gemsTotal += gems;
    s.stats.blitzGames++;
    const xpTo = levelInfo(s.xp);
    for (let lv = xpFrom.level + 1; lv <= xpTo.level; lv++) s.coins += 20 + lv * 5;
    saves.persist(true);
    this.state = 'results';
    this.input.showTouchControls(false);
    this.ui.blitzHud.hide();
    this.ui.showBlitzResults({ ...r, coins, gems, xp, newBest });
    if (newBest) audio.play('levelUp');
    if (newBest) haptics.play('levelUp');
    this.checkBadges();
    (async () => {
      for (let lv = xpFrom.level + 1; lv <= xpTo.level; lv++) await this.ui.showLevelUp(lv, rankFor(lv).name !== rankFor(lv - 1).name, 20 + lv * 5);
    })();
  }

  // ---------------- economy ----------------
  async lockerItem(cat, id, el) {
    const s = saves.data;
    const it = catalogItem(cat, id);
    if (!it) return;
    const itemName = withName(it.name, escapeHtml(s.name));
    if (s.owned[cat].includes(id)) {
      if (s.equip[cat] === id) return;
      s.equip[cat] = id;
      audio.play('pop');
      saves.persist();
      this.refreshCharacter();
      this.ui.showLocker(cat);
      return;
    }
    if((it.learn||0)>s.stats.correct){audio.play('wrong');this.ui.toast(`Learn ${it.learn-s.stats.correct} more answers in any mode to unlock ${itemName}.`,'blue');return;}
    if (it.unlock && !unlockMet(s, it.unlock)) {
      audio.play('wrong');
      this.ui.toast(`🔒 ${it.desc || 'Locked for now!'}`, 'dark');
      return;
    }
    if ((it.unlock && unlockMet(s, it.unlock)) || it.price === 0) {
      s.owned[cat].push(id);
      s.equip[cat] = id;
      audio.play('unlock');
      saves.persist();
      this.refreshCharacter();
      this.ui.showLocker(cat);
      this.checkBadges();
      return;
    }
    const cur = it.cur === 'gems' ? 'gems' : 'coins';
    if (s[cur] < it.price) {
      audio.play('wrong');
      el && el.classList.add('shake');
      setTimeout(() => el && el.classList.remove('shake'), 500);
      const need = it.price - s[cur];
      this.ui.toast(`You need ${fmtNum(need)} more ${cur === 'gems' ? '💎 gems' : '🪙 coins'}! Play levels and Brain Blitz to earn more!`, 'dark', 3800);
      return;
    }
    audio.play('click');
    const ok = await this.ui.confirm(`Buy ${itemName}?`, `It costs ${cur === 'gems' ? ICON.gem : ICON.coin} <b>${fmtNum(it.price)}</b>. You have ${fmtNum(s[cur])}.`, 'Buy it!', 'Not now');
    this.ui.closeModal();
    if (!ok) return;
    s[cur] -= it.price;
    s.owned[cat].push(id);
    s.equip[cat] = id;
    saves.persist(true);
    audio.play('buy');
    haptics.play('buy');
    this.refreshCharacter();
    this.ui.showLocker(cat);
    const p = this.menuScene.ch ? this.menuScene.ch.root.position.clone().add(new THREE.Vector3(0, 1.6, 0)) : new THREE.Vector3(0, 42, 0);
    this.fx.particles.confetti(p, 70, 1.2);
    this.ui.toast(`🎉 You got the <b>${itemName}</b>!`, 'green');
    this.checkBadges();
  }

  claimDaily(btn) {
    const s = saves.data;
    const today = todayStr();
    if (s.daily.lastClaim === today) return;
    const consecutive = s.daily.lastClaim && daysBetween(s.daily.lastClaim, today) === 1;
    if (!consecutive) {
      s.daily.streak = 0;
      s.daily.day = 0;
    }
    const r = DAILY[s.daily.day % 7];
    s.daily.streak++;
    s.daily.bestStreak = Math.max(s.daily.bestStreak || 0, s.daily.streak);
    s.daily.day++;
    s.daily.lastClaim = today;
    s.coins += r.coins || 0;
    s.gems += r.gems || 0;
    s.stats.coinsTotal += r.coins || 0;
    s.stats.gemsTotal += r.gems || 0;
    saves.persist(true);
    audio.play('chest');
    haptics.play('reward');
    setTimeout(() => audio.play('buy'), 350);
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = `+${r.coins ? fmtNum(r.coins) + ' ' + ICON.coin : ''} ${r.gems ? r.gems + ' ' + ICON.gem : ''}`;
    }
    const p = this.menuScene.ch ? this.menuScene.ch.root.position.clone().add(new THREE.Vector3(0, 2, 0)) : new THREE.Vector3(0, 42, 0);
    this.fx.particles.confetti(p, 80, 1.5);
    setTimeout(() => {
      this.ui.closeModal();
      if (this.ui.screen === 'main') this.ui.showMain();
      this.checkBadges();
    }, 1400);
  }

  onAnswer(correct, firstTry, firstAttempt = firstTry, challenge = false) {
    const st = saves.data.stats;
    if (firstAttempt) st.answered++;
    if (correct) {
      st.correct++;
      if (firstTry) st.firstTry++;
      // A lesson's 🔥 challenge counts as won when it is right first time, without a hint.
      if (firstTry && challenge) st.challenges = (st.challenges || 0) + 1;
    }
    // streak shared across quiz, boss and blitz
    if (firstTry && this.quiz) {
      if (this.state === 'level' && this.level && this.level.state !== 'quiz') this.quiz.streak = correct ? this.quiz.streak + 1 : 0;
      if (this.state === 'blitz') this.quiz.streak = correct ? this.quiz.streak + 1 : 0;
      st.bestStreak = Math.max(st.bestStreak, this.quiz.streak);
    }
  }

  checkBadges() {
    const s = saves.data;
    for (const b of BADGES) {
      if (s.ach[b.id]) continue;
      let ok = false;
      try {
        ok = b.test(s, this.qe);
      } catch (e) {
        ok = false;
      }
      if (!ok) continue;
      s.ach[b.id] = Date.now();
      s.coins += b.reward.coins || 0;
      s.gems += b.reward.gems || 0;
      this.badgeQueue.push(b);
    }
    saves.persist();
    if (!this.badgeTimer) this.flushBadges();
  }

  flushBadges() {
    const b = this.badgeQueue.shift();
    if (!b) {
      this.badgeTimer = null;
      return;
    }
    this.ui.badgeToast(b);
    haptics.play('badge');
    this.badgeTimer = setTimeout(() => this.flushBadges(), 1600);
  }

  // Signing in can load a different player's game onto this device.
  afterSaveSwap() {
    this.skinCache.clear();
    this.quiz.streak = 0;
    audio.setVolumes(saves.data.settings.music, saves.data.settings.sfx);
    this.camRig.setDistance(saves.data.settings.cameraDistance);
    this.applyQuality();
    this.refreshCharacter();
  }

  afterRestore() {
    this.skinCache.clear();
    audio.setVolumes(saves.data.settings.music, saves.data.settings.sfx);
    this.refreshCharacter();
    this.camRig.setDistance(saves.data.settings.cameraDistance);
    this.ui.toast('✅ Save loaded!', 'green');
    this.showMenu();
  }

  resetProgress() {
    saves.resetAll();
    this.skinCache.clear();
    this.quiz.streak = 0;
    this.refreshCharacter();
    this.ui.toast('Progress reset. A fresh adventure begins! 🌱', 'blue');
    this.showMenu();
  }

  // ---------------- loop ----------------
  loop(now) {
    requestAnimationFrame((t) => this.loop(t));
    let dt = (now - this.last) / 1000;
    this.last = now;
    if (this.testHold) return; // the test harness is driving the game
    if (dt > 0.1) dt = 0.1;
    if (dt <= 0) dt = 0.001;
    this.tick(dt, true);
  }

  // one frame of game logic (+ optional render). The test harness calls this directly.
  tick(dt, render = true) {
    const fdt = Math.min(dt, 0.05);
    this.time += fdt;
    const input = this.input;
    input.update();
    if (this.testInput) {
      Object.assign(input, this.testInput.held);
      for (const k of this.testInput.pressed) input.pressed.add(k);
      this.testInput.pressed = [];
    }
    if(this.paused&&this.explore&&this.ui.modalEl.classList.contains('modal-inventory')&&input.wasPressed('i'))this.resume();
    if (input.pausePressed) {
      if (this.paused) this.resume();
      else if (this.ui.modalOpen && this.state === 'menu') this.ui.closeModal();
      else this.pause();
    }
    if (!this.paused) {
      if (this.state === 'level' && this.level) this.level.update(fdt, input);
      else if (this.state === 'results' && this.level) this.level.update(fdt, NO_INPUT_G);
      else if (this.state === 'explore' && this.explore) this.explore.update(fdt, input);
      else if (this.state === 'blitz' && this.blitz) this.blitz.update(fdt, input);
      else if (this.state === 'results' && this.blitz) this.blitz.update(fdt, NO_INPUT_G);
      else if (this.menuScene && this.menuScene.group.visible) this.menuScene.update(fdt, input);
      if (this.state === 'level' || this.state === 'blitz' || this.state === 'explore') saves.data.stats.playTime += fdt;
    }
    // Friends keep moving while your inventory, map or help is open.
    if (this.mp && this.state === 'explore') this.mp.update(fdt);
    document.body.classList.toggle('playing', ['level', 'blitz', 'explore'].includes(this.state));
    document.body.classList.toggle('game-paused', this.paused);
    if (!this.blitz && saves.data.settings.cameraDistance !== this.camRig.dist) {
      saves.data.settings.cameraDistance = this.camRig.dist;
      clearTimeout(this.zoomSaveTimer);
      this.zoomSaveTimer = setTimeout(() => saves.persist(), 400);
    }
    this.fx.particles.update(fdt);
    this.fx.rings.update(fdt);
    this.fx.beams.update(fdt);
    this.fx.worldUI.update(fdt);
    this.sky.update(fdt, this.camera);
    this.waterTex.offset.x = (this.time * 0.07) % 1;
    this.waterTex.offset.y = (this.time * 0.11) % 1;
    this.lavaTex.offset.y = (-this.time * 0.035) % 1;
    this.lavaTex.offset.x = (Math.sin(this.time * 0.3) * 0.05) % 1;
    this.updateSun();
    this.ambientParticles(fdt);
    if (render) this.composer.render(dt);
    input.endFrame();
    if (render) this.trackFps(dt);
  }

  updateSun() {
    const focus = this.state === 'explore' && this.explore ? this.explore.player.pos : this.state === 'level' && this.level ? this.level.player.pos : this.state === 'blitz' && this.blitz ? this.blitz.player.pos : this.menuScene?.camLook || new THREE.Vector3();
    const texel = 68 / this.sun.shadow.mapSize.x;
    const fx = Math.round(focus.x / texel) * texel, fy = Math.round(focus.y / texel) * texel, fz = Math.round(focus.z / texel) * texel;
    this.sun.target.position.set(fx, fy, fz);
    this.sun.position.set(fx + this.sunDir.x * 80, fy + this.sunDir.y * 80, fz + this.sunDir.z * 80);
  }

  ambientParticles(dt) {
    if (this.paused || this.qualityLevel === 'low') return;
    const kind = this.ambient;
    if (!kind) return;
    const P = this.fx.particles;
    const c = this.camera.position;
    const rate = { leaves: 4, dust: 6, snow: 40, embers: 22, glitch: 14 }[kind] || 0;
    let n = rate * dt;
    while (n > 0) {
      if (Math.random() > n) break;
      n -= 1;
      const x = c.x + (Math.random() - 0.5) * 40, z = c.z + (Math.random() - 0.5) * 40;
      if (kind === 'snow') P.spawn({ x, y: c.y + 10 + Math.random() * 6, z, vx: 0.6, vy: -2.2, life: 6, size: 0.08, color: '#ffffff', gravity: 0, drag: 0, glow: true, bright: 1.3, shrink: false });
      else if (kind === 'embers') P.spawn({ x, y: c.y - 8 - Math.random() * 6, z, vx: (Math.random() - 0.5), vy: 2 + Math.random() * 2, life: 5, size: 0.08, color: Math.random() < 0.5 ? '#ff8a20' : '#ffd040', gravity: -0.2, drag: 0, glow: true, bright: 2.2, shrink: false });
      else if (kind === 'glitch') P.spawn({ x, y: c.y + (Math.random() - 0.5) * 12, z, vy: 0.3, life: 3, size: 0.1, color: Math.random() < 0.5 ? '#ff2bd6' : '#22e8ff', gravity: 0, drag: 0, glow: true, bright: 1.8, spin: 0 });
      else if (kind === 'leaves') P.spawn({ x, y: c.y + 8 + Math.random() * 4, z, vx: 1, vy: -1.2, life: 7, size: 0.12, sx: 1, sy: 0.25, sz: 1, color: Math.random() < 0.6 ? '#5fae3a' : '#c8d84a', gravity: 0, drag: 0, spin: 3, shrink: false });
      else if (kind === 'dust') P.spawn({ x, y: c.y + (Math.random() - 0.5) * 8, z, vx: 2.5, vy: 0.1, life: 5, size: 0.05, color: '#f0d8a8', gravity: 0, drag: 0, shrink: false });
    }
  }

  trackFps(dt) {
    if (saves.data.settings.quality !== 'auto' || !['level', 'blitz', 'explore'].includes(this.state) || this.paused || this.quiz.open) return;
    this.fpsAcc += dt;
    this.fpsFrames++;
    if (this.fpsAcc > 4) {
      const fps = this.fpsFrames / this.fpsAcc;
      this.fpsAcc = 0;
      this.fpsFrames = 0;
      if (fps < 38 && this.qualityLevel !== 'low') {
        const next = this.qualityLevel === 'high' ? 'medium' : 'low';
        this.qualityLevel = next;
        this.applyQuality(next);
        this.ui.toast('⚙️ Graphics adjusted to keep things smooth', 'dark', 2500);
      }
    }
  }
}

function joinFailText(code, owner = 'your friend') {
  return {
    4001: 'Please sign in again, then try once more.',
    4002: `${owner}’s world is locked right now. Ask them to unlock it.`,
    4003: `${owner} asked visitors to leave for now. Your own world is always ready!`,
    4004: `${owner}’s world is full right now. Try again soon!`,
    4005: 'We couldn’t find that world. Check the code with your friend.',
  }[code] || `We couldn’t reach ${owner}’s world. Try again in a moment.`;
}

const NO_INPUT_G = { moveX: 0, moveY: 0, jumpPressed: false, jumpHeld: false, actionPressed: false, pausePressed: false, camDX: 0, camDY: 0, wheel: 0, taps: [], touchMode: false, wasPressed: () => false };

function nextFrame() {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

const game = new Game();
window.__game = game;
installTestHooks(game);
game.init().catch((e) => {
  console.error(e);
  const tip = document.getElementById('load-tip');
  if (tip && !tip.textContent.includes('WebGL')) tip.textContent = 'Something went wrong loading the game. Please refresh the page! (' + (e && e.message) + ')';
});
void DAILY;
void unlockMet;
